const desktopProductCarousels = new WeakMap();
const desktopProductCarouselQuery = window.matchMedia('(min-width: 960px)');
const mobileProductCarouselQuery = window.matchMedia('(max-width: 959px)');
const fallbackMobileCarousels = new WeakMap();

const syncDesktopProductCarousel = (gallery, activeIndex) => {
  const thumbnails = [...gallery.querySelectorAll('[data-product-thumbnail]')];

  thumbnails.forEach((thumbnail, index) => {
    thumbnail.classList.toggle('active', index === activeIndex);
  });

  const activeThumbnail = thumbnails[activeIndex]?.closest('.product-thumbnails__item');
  const viewport = gallery.querySelector('[data-scroll-container-viewport]');

  if (activeThumbnail && viewport) {
    const viewportBounds = viewport.getBoundingClientRect();
    const thumbnailBounds = activeThumbnail.getBoundingClientRect();

    if (thumbnailBounds.left < viewportBounds.left) {
      viewport.scrollLeft += thumbnailBounds.left - viewportBounds.left;
    } else if (thumbnailBounds.right > viewportBounds.right) {
      viewport.scrollLeft += thumbnailBounds.right - viewportBounds.right;
    }
  }
};

const initDesktopProductCarousel = async gallery => {
  if (desktopProductCarousels.has(gallery) || !window.flu?.chunks?.swiper) return;
  if (gallery.classList.contains('above-mobile') && !desktopProductCarouselQuery.matches) return;

  const slides = [...gallery.querySelectorAll('.swiper-slide')];
  if (slides.length < 2) return;

  const activeMediaId = gallery.querySelector('[data-product-thumbnail].active')?.dataset.thumbnailId;
  const initialSlide = Math.max(
    0,
    slides.findIndex(slide => slide.dataset.mediaItemId === activeMediaId)
  );
  const { Swiper } = await import(window.flu.chunks.swiper);
  const carousel = new Swiper(gallery.querySelector('.product-media-carousel'), {
    slidesPerView: 1,
    spaceBetween: 4,
    grabCursor: true,
    autoHeight: true,
    initialSlide,
    watchSlidesProgress: true
  });

  desktopProductCarousels.set(gallery, carousel);
  syncDesktopProductCarousel(gallery, carousel.activeIndex);
  carousel.on('slideChange', () => syncDesktopProductCarousel(gallery, carousel.activeIndex));

  const observeSlides = () => {
    slides.forEach(slide => slideObserver.observe(slide, { attributes: true, attributeFilter: ['class'] }));
  };
  const slideObserver = new MutationObserver(() => {
    const selectedIndex = slides.findIndex(slide => !slide.classList.contains('hidden'));
    if (selectedIndex < 0 || !slides.some(slide => slide.classList.contains('hidden'))) return;

    slideObserver.disconnect();
    slides.forEach(slide => slide.classList.remove('hidden'));
    carousel.slideTo(selectedIndex);
    observeSlides();
  });
  observeSlides();
};

const initDesktopProductCarousels = root => {
  const galleries = [
    ...(root.matches?.('[data-product-carousel]') ? [root] : []),
    ...root.querySelectorAll('[data-product-carousel]')
  ];
  galleries.forEach(gallery => initDesktopProductCarousel(gallery));
};

document.addEventListener('click', event => {
  const control = event.target.closest(
    "[data-product-carousel] [data-product-thumbnail], [data-product-carousel] .scroll-button"
  );

  if (!control) return;

  const gallery = control.closest('.product__media-container');
  const carousel = desktopProductCarousels.get(gallery);
  if (!carousel) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (control.matches('[data-product-thumbnail]')) {
    const thumbnails = [...gallery.querySelectorAll('[data-product-thumbnail]')];
    carousel.slideTo(thumbnails.indexOf(control));
  } else if (control.dataset.direction === 'backwards') {
    carousel.isBeginning ? carousel.slideTo(carousel.slides.length - 1) : carousel.slidePrev();
  } else {
    carousel.isEnd ? carousel.slideTo(0) : carousel.slideNext();
  }
}, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initDesktopProductCarousels(document));
} else {
  initDesktopProductCarousels(document);
}

document.addEventListener('shopify:section:load', event => initDesktopProductCarousels(event.target));
desktopProductCarouselQuery.addEventListener('change', event => {
  if (event.matches) initDesktopProductCarousels(document);
});

new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) initDesktopProductCarousels(node);
    });
  });
}).observe(document.body, { childList: true, subtree: true });

/* The theme normally initializes this carousel itself. This fallback only runs
   when that initialization did not happen, which otherwise leaves the existing
   pagination element empty and the mobile dots invisible. */
const initFallbackMobileProductCarousels = async root => {
  if (!mobileProductCarouselQuery.matches || !window.flu?.chunks?.swiper) return;

  const carousels = [
    ...(root.matches?.('.product[data-is-full-product="true"] .product__media-container.below-mobile.carousel')
      ? [root]
      : []),
    ...root.querySelectorAll(
      '.product[data-is-full-product="true"] .product__media-container.below-mobile.carousel'
    )
  ];

  for (const carouselElement of carousels) {
    if (
      carouselElement.classList.contains('swiper-initialized') ||
      fallbackMobileCarousels.has(carouselElement)
    ) {
      continue;
    }

    const slides = [...carouselElement.querySelectorAll('.swiper-slide')];
    if (slides.length < 2) continue;

    const productElement = carouselElement.closest('.product');
    const hideDots = productElement?.dataset.hideMobileCarouselDots === 'true';
    const loop = productElement?.dataset.loopMobileCarousel === 'true';
    const featuredSlide = slides.find(slide => slide.dataset.isFeatured === 'true');
    const initialSlide = featuredSlide ? slides.indexOf(featuredSlide) : 0;
    const { Swiper, Pagination } = await import(window.flu.chunks.swiper);

    // The native theme initializer may have completed while the chunk loaded.
    if (carouselElement.classList.contains('swiper-initialized')) continue;

    const carousel = new Swiper(carouselElement, {
      modules: [Pagination],
      slidesPerView: 1,
      spaceBetween: 4,
      grabCursor: true,
      pagination: hideDots
        ? {}
        : {
            el: carouselElement.querySelector('.swiper-pagination'),
            type: 'bullets',
            dynamicBullets: true,
            dynamicMainBullets: 7,
            clickable: true
          },
      watchSlidesProgress: true,
      loop,
      autoHeight: true,
      initialSlide
    });

    fallbackMobileCarousels.set(carouselElement, carousel);
  }
};

const scheduleFallbackMobileProductCarousels = root => {
  window.setTimeout(() => initFallbackMobileProductCarousels(root), 250);
};

if (document.readyState === 'complete') {
  scheduleFallbackMobileProductCarousels(document);
} else {
  window.addEventListener('load', () => scheduleFallbackMobileProductCarousels(document), { once: true });
}

mobileProductCarouselQuery.addEventListener('change', event => {
  if (event.matches) scheduleFallbackMobileProductCarousels(document);
});

document.addEventListener('shopify:section:load', event => {
  scheduleFallbackMobileProductCarousels(event.target);
});
